import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Button, Grid, TextField, Typography, useTheme, CircularProgress } from "@mui/material";
import { Card } from "../../ui";
import { styled } from "../../../theme";
import useValidate from "../../../hooks/useValidate";
import { validationProfileSupport } from "../validations";
import { postSupportMessage, clearPostSupportMessageLS } from "../../../reducers/Profile";
import { isLoading } from "../../../constants/redux.constants";
import { showNotification } from "../../../reducers/App";
import { getUrlParamEncoded } from "../utils/getUrlParamEncoded";



const supportOptions = [
  { value: 'COMMON',  title: 'Общее замечание' },
  { value: 'ENHANCE', title: 'Предложение по улучшению' },
  { value: 'ERROR',   title: 'Сообщение об ошибке' },
]

function SupportTab() {
  document.title = "Д24 | Служба поддержки";
  const dispatch = useDispatch();
  const theme = useTheme();

  const token = getUrlParamEncoded('token');

  const { profile, postSupportMessageLS } = useSelector(state => state.profile)

  useEffect(() => {
  
    return () => {
      dispatch(clearPostSupportMessageLS())
    }
  }, [])

  const [submitData, setSubmitData] = useState({ 
    type:     null,
    title:    null,
    message:  null
  })

  // Валидация формы
  const { validate, errors } = useValidate({ 
    schema: validationProfileSupport, 
    data: {
      ...submitData, 
      type:     submitData.type      ? String(submitData.type).trim()      : undefined, 
      title:    submitData.title     ? String(submitData.title).trim()     : undefined, 
      message:  submitData.message   ? String(submitData.message).trim()   : undefined,
    }
  })

  const submit = async(e) => {
    if (e) e.preventDefault();

    if(validate() && !isLoading(postSupportMessageLS)) {
      const params = {
        type:       submitData.type,
        title:      submitData.title,
        message:    submitData.message,
        username:   profile.username,
        position:   profile.ref_users.POSITION
      }

      const result = await dispatch(postSupportMessage(params));
    
      if(result.payload?.ok) {
        dispatch(showNotification({ isOpen: true, message: 'Обращение успешно отправлено!', severity: 'success' }))
      }
      setSubmitData({ message: null, title: null, type: null })
    }

  }

  return (
    <Box>
      <Typography variant="h4" sx={{ pl: { xs: 0.5, md: 0 }, display: token ? 'none' : 'inline-flex' }} pb={3}>Служба поддержки</Typography>

      <Grid container xs={12} md={8} gap={{ xs: 1, md: 2 }}>
        <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minWidth: '100%', 
            border: `1px solid ${theme.palette.OICO.stone[200]}`,
            p: { xs: 2, md: 3 },
            mb: { xs: 1, md: 2 },
            gap: 1
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: 14 }} pb={3}>Опишите вашу проблему или предложение и мы обязательно рассмотрим ваше обращение</Typography>

            <form onSubmit={(e) => submit(e)}>
              <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container gap={2}>

                  <Grid xs={12} item display='flex' position='relative' direction='column' gap={0.5}>
                    <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Тип обращения *</Typography>
                    <Autocomplete size="small" fullWidth
                      disabled={isLoading(postSupportMessageLS)}
                      getOptionLabel={(option) => option.title}
                      disableClearable
                      value={ supportOptions.find((item) => item.value === submitData.type) || null}
                      options={supportOptions}
                      onChange={(e, newValue) => setSubmitData((prevData) => ({ ...prevData, type: newValue?.value ?? null, }))}
                      renderInput={(params) => (
                        <TextField 
                          placeholder="Выберите тип обращения"
                          {...params} 
                          sx={{ ...styled('autocomplete',"input", "filled") }}    
                          error={!!errors.type}
                          helperText={errors.type}
                        />
                      )}
                    />
                  </Grid>

                  <Grid xs={12} item display='flex' position='relative' direction='column' gap={0.5}>
                    <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Заголовок *</Typography>
                    <Typography position='absolute' right={2} fontSize={14} color={'grey.scale70'}>{`${(submitData.title || '').length} / 100`}</Typography>
                    <TextField fullWidth size="small"    
                      disabled={isLoading(postSupportMessageLS)}  
                      value={submitData.title ?? ''}
                      onChange={(e) => setSubmitData((prevData) => ({ ...prevData, title: e.target.value  }))}
                      sx={{ ...styled('autocomplete',"input", "filled") }}    
                      error={!!errors.title}
                      helperText={errors.title}
                      inputProps={{ maxLength: 100 }}     
                      placeholder="Введите заголовок" 
                    />
                  </Grid>

                  <Grid xs={12} item display='flex' position='relative' direction='column' gap={0.5}>
                    <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Текст обращения *</Typography>
                    <Typography position='absolute' right={2} fontSize={14} color={'grey.scale70'}>{`${(submitData.message || '').length} / 1000`}</Typography>
                    <TextField fullWidth size="small"    
                      disabled={isLoading(postSupportMessageLS)}  
                      value={submitData.message ?? ''}
                      onChange={(e) => setSubmitData((prevData) => ({ ...prevData, message: e.target.value  }))}
                      sx={{ ...styled('autocomplete',"input", 'filled'), '& .MuiInputBase-root': { fontSize: 14 } }}    
                      multiline
                      minRows={4}
                      maxRows={8}
                      error={!!errors.message}
                      helperText={errors.message}
                      inputProps={{ maxLength: 1000 }}     
                      placeholder="Текст обращения" 
                    />
                  </Grid>
                  <Grid xs={12} item display='flex' alignItems='center' justifyContent='space-between' gap={1.5} pt={2}>
                    <Button 
                      disabled={(isLoading(postSupportMessageLS) || !(submitData.message && submitData.title && submitData.type))} 
                      sx={{ textTransform: 'none', paddingX: '30px', paddingY: '12px', width: { xs: '100%', md: 'auto' } }} 
                      disableElevation 
                      variant="contained" 
                      color={"green"} 
                      type="submit"
  
                    >
                      {isLoading(postSupportMessageLS) && (<Box  sx={{ alignItems: 'center', display: 'flex' }} pr={1}><CircularProgress size={15} color='inherit' /></Box>)}
                      Отправить
                    </Button>
                  </Grid>

                </Grid>
              </Grid>
            </form>
          </Card>
      </Grid>
    </Box>
  )
}

export default SupportTab;